import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, Input, Select, Textarea, SearchField } from '@dreamtree-org/twreact-ui';
import { Plus, Search } from 'lucide-react';
import Can from '@rbac/Can';
import { PageHeader, SectionCard, QueryState, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { TICKET_STATUS, CHANNEL_LABEL, PRIORITY, STORAGE_ACCESS } from '../domain';
import { DataTable, PriorityBadge, TicketStatusBadge, formatDateTime, useAction, ActionResult } from '../components';

const INTAKE_CHANNELS = ['walk_in', 'phone', 'whatsapp'].map((value) => ({ value, label: CHANNEL_LABEL[value] }));
const PRIORITY_OPTIONS = Object.entries(PRIORITY).map(([value, m]) => ({ value, label: m.label }));
const ACCESS_OPTIONS = Object.entries(STORAGE_ACCESS).map(([value, m]) => ({ value, label: m.label }));
const EMPTY = { customerName: '', customerPhone: '', deviceModel: '', serial: '', problem: '', description: '', channel: 'walk_in', priority: 'normal', storageAccess: 'none', address: '', pin: '' };

/** Intake at the counter or over the phone: a ticket the desk opens itself. */
function NewTicketDialog({ open, onClose, onCreated }) {
  const act = useAction();
  const [f, setF] = useState(EMPTY);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const ready = f.customerName.trim() && /^[6-9]\d{9}$/.test(f.customerPhone.replace(/\D/g, '').slice(-10)) && f.deviceModel.trim() && f.problem.trim();
  const submit = async () => {
    const res = await act.run('serviceTicketOpen', f, '', 'open');
    if (res) {
      setF(EMPTY);
      onCreated(res.data);
    }
  };
  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      title="New service ticket"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!ready} loading={act.busy === 'open'}>Open ticket</Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select label="Channel" options={INTAKE_CHANNELS} value={f.channel} onChange={(v) => set('channel', v || 'walk_in')} closeOnSelect />
        <Select label="Priority" options={PRIORITY_OPTIONS} value={f.priority} onChange={(v) => set('priority', v || 'normal')} closeOnSelect />
        <Input label="Customer name" value={f.customerName} onChange={(e) => set('customerName', e.target.value)} required />
        <Input label="Mobile number" type="tel" inputMode="numeric" value={f.customerPhone} onChange={(e) => set('customerPhone', e.target.value)} helperText="Handover codes are texted to this number." required />
        <Input label="Device model" value={f.deviceModel} onChange={(e) => set('deviceModel', e.target.value)} placeholder="e.g. HP Pavilion x360 14" required />
        <Input label="Serial number (optional)" value={f.serial} onChange={(e) => set('serial', e.target.value)} />
        <div className="sm:col-span-2"><Input label="Problem" value={f.problem} onChange={(e) => set('problem', e.target.value)} placeholder="e.g. No power" required /></div>
        <div className="sm:col-span-2"><Textarea label="What the customer says" rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} /></div>
        <div className="sm:col-span-2"><Select label="Storage access the customer allows" options={ACCESS_OPTIONS} value={f.storageAccess} onChange={(v) => set('storageAccess', v || 'none')} closeOnSelect /></div>
        {f.channel !== 'walk_in' ? (
          <>
            <Input label="Pickup address" value={f.address} onChange={(e) => set('address', e.target.value)} />
            <Input label="PIN code" inputMode="numeric" maxLength={6} value={f.pin} onChange={(e) => set('pin', e.target.value.replace(/\D/g, ''))} />
          </>
        ) : null}
      </div>
      <div className="mt-3"><ActionResult action={act} /></div>
    </Dialog>
  );
}

const STATUS_OPTIONS = [{ value: 'all', label: 'All statuses' }, ...Object.entries(TICKET_STATUS).map(([value, m]) => ({ value, label: m.label }))];

/** R-01 Service tickets: what the customer reported, before (and beside) the work order. */
export default function Tickets() {
  const nav = useNavigate();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const q = useQuery('serviceTickets', { status, q: search }, { select: (r) => r.data });

  const columns = [
    {
      key: 'id',
      label: 'Ticket',
      render: (t) => (
        <div>
          <div className="font-semibold">{t.id}</div>
          <div className="text-xs opacity-70">{formatDateTime(t.openedAt)}</div>
        </div>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      render: (t) => (
        <div>
          <div>{t.customer.name}</div>
          <div className="text-xs opacity-70">{t.location.area}</div>
        </div>
      ),
    },
    {
      key: 'device',
      label: 'Device',
      render: (t) => (
        <div>
          <div>{t.device.model}</div>
          <div className="text-xs opacity-70 font-mono">{t.device.serialMasked}</div>
        </div>
      ),
    },
    { key: 'problem', label: 'Reported problem', render: (t) => t.problem },
    { key: 'channel', label: 'Channel', hideOnMobile: true, render: (t) => CHANNEL_LABEL[t.channel] || t.channel },
    { key: 'priority', label: 'Priority', render: (t) => <PriorityBadge priority={t.priority} /> },
    { key: 'status', label: 'Status', render: (t) => <TicketStatusBadge status={t.status} /> },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Service tickets"
        subtitle="Customer-reported problems. A ticket becomes a work order once the device is in our custody."
        actions={
          <Can module="service" action="create">
            <Button onClick={() => setCreating(true)} leftIcon={<Plus size={16} />}>New ticket</Button>
          </Can>
        }
      />
      <NewTicketDialog open={creating} onClose={() => setCreating(false)} onCreated={(t) => nav(`/tickets/${t.id}`)} />
      <SectionCard>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_16rem]">
          <SearchField
            placeholder="Search ticket, customer, model or problem"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search size={16} />}
            aria-label="Search tickets" />
          <Select options={STATUS_OPTIONS} value={status} onChange={(v) => setStatus(v || 'all')} closeOnSelect aria-label="Filter by status" />
        </div>
        <QueryState query={q}>
          {(rows) =>
            rows.length === 0 ? (
              <EmptyState title="No tickets match">Try another status or search term.</EmptyState>
            ) : (
              <DataTable data={rows} columns={columns} onRowClick={(t) => nav(`/tickets/${t.id}`)} mobileMaxFields={5} />
            )
          }
        </QueryState>
      </SectionCard>
    </div>
  );
}
